import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { KeyRound } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function ForgotPassword() {
    const [email, setEmail] = useState("");
    const [token, setToken] = useState("");
    const [newPass, setNewPass] = useState("");
    const [phase, setPhase] = useState("request");
    const [busy, setBusy] = useState(false);

    const request = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const { data } = await api.post("/auth/forgot-password", { email });
            if (data.dev_reset_token) {
                setToken(data.dev_reset_token);
                toast("Dev reset token", {
                    description: data.dev_reset_token,
                    duration: 10000,
                });
                setPhase("reset");
            } else {
                toast.info("If the email is registered, a reset link was sent.");
                setPhase("reset");
            }
        } catch (err) {
            toast.error(errMsg(err, "Request failed"));
        } finally {
            setBusy(false);
        }
    };

    const reset = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await api.post("/auth/reset-password", {
                email,
                reset_token: token,
                new_password: newPass,
            });
            toast.success("Password reset — you can now log in.");
            setPhase("done");
        } catch (err) {
            toast.error(errMsg(err, "Reset failed"));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#F5F7FB] flex items-center justify-center p-6 relative">
            <div className="absolute inset-0 ddi-dot-grid-dark opacity-40 pointer-events-none" aria-hidden />
            <div className="ddi-card ddi-shadow-md p-8 sm:p-9 w-full max-w-md relative" data-testid="forgot-panel">
                <Link to="/" className="flex items-center gap-2.5 w-fit" data-testid="forgot-brand">
                    <img src="/brand-icon.png" alt="Doctor Digital ID" className="h-10 w-10 object-contain" />
                    <span className="text-[15px] font-bold tracking-tight text-[#123B5D]">
                        DOCTOR <span className="text-[#2563EB]">DIGITAL ID</span>
                    </span>
                </Link>
                <div className="mt-6 inline-flex items-center justify-center h-11 w-11 rounded-xl bg-[#0F766E]/10 text-[#0F766E]">
                    <KeyRound size={18} />
                </div>
                <h1 className="mt-4 text-[26px] font-bold text-[#123B5D] tracking-tight">
                    Reset your password
                </h1>
                <p className="mt-1.5 text-[13.5px] text-[#64748B]">
                    We'll email you a reset token. Enter it below with your new password.
                </p>
                {phase === "request" ? (
                    <form onSubmit={request} className="mt-6 space-y-4">
                        <div>
                            <Label>Email</Label>
                            <Input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                data-testid="forgot-email"
                                required
                            />
                        </div>
                        <button
                            className="w-full ddi-teal-btn disabled:opacity-60"
                            disabled={busy}
                            data-testid="forgot-submit"
                        >
                            {busy ? "Sending…" : "Send reset token"}
                        </button>
                    </form>
                ) : phase === "reset" ? (
                    <form onSubmit={reset} className="mt-6 space-y-4">
                        <div>
                            <Label>Reset token (dev)</Label>
                            <Input
                                value={token}
                                onChange={(e) => setToken(e.target.value)}
                                data-testid="forgot-token"
                                required
                            />
                        </div>
                        <div>
                            <Label>New password</Label>
                            <Input
                                type="password"
                                value={newPass}
                                onChange={(e) => setNewPass(e.target.value)}
                                data-testid="forgot-new-pass"
                                required
                                minLength={8}
                            />
                        </div>
                        <button
                            className="w-full ddi-teal-btn disabled:opacity-60"
                            disabled={busy}
                            data-testid="forgot-reset-submit"
                        >
                            {busy ? "Resetting…" : "Set new password"}
                        </button>
                    </form>
                ) : (
                    <div className="mt-6 text-sm text-[#17212B]/80" data-testid="forgot-done">
                        Password updated. Head to <Link to="/login" className="text-[#0F766E] font-semibold hover:underline">login</Link>.
                    </div>
                )}
                <div className="mt-4 text-sm">
                    <Link to="/login" className="text-[#17212B]/60 hover:text-[#123B5D]">
                        Back to login
                    </Link>
                </div>
            </div>
        </div>
    );
}
